import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_MongoId, T_Populate, T_Replace } from "types/_utils.types";
import { LeadDto, LeadSchemaDto, LeadStatus } from "dtos/lead.dto";
import {
  Assignment,
  BATCH_TYPE,
  CASE_STATUS,
  CaseBatchSchemaDto,
  CaseSchemaDto,
  PROCESS_STATUS,
} from "dtos/leadCases.dto";
import { UserSchemaDto } from "dtos/user.dto";
import { queryClient } from "utils/queryClient";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { formatDate } from "utils/functions";
import dayjs from "dayjs";
import { ElementOf } from "types/_utils.types";

type PopulatedLead = T_Replace<LeadSchemaDto, "assigned_to", Pick<UserSchemaDto, "_id" | "name" | "email">>;

export const leadService = {
  useGetAllPaginated: (params: {
    page: number;
    limit: number;
    filter?: Partial<
      Record<keyof LeadDto | `questionaire.${keyof NonNullable<LeadSchemaDto["questionaire"]>}`, string[]>
    >;
    sort?: Partial<Record<keyof LeadDto, 1 | -1>>;
    startDate?: string;
    endDate?: string;
    search?: string;
  }) => {
    return useQuery({
      queryKey: ["lead/all", params],
      queryFn: async () => {
        const res = await api.get(`/lead`, { params });
        return res.data?.result as T_Api_Paginated<PopulatedLead>;
      },
    });
  },
  useGetByIdOrRayId: (id: string | undefined, lead?: PopulatedLead) => {
    return useQuery({
      queryKey: ["lead", id],
      queryFn: async () => {
        const res = await api.get(`/lead/${id}`);
        return res.data?.result as PopulatedLead;
      },
      enabled: !!id,
      placeholderData: lead,
    });
  },
  useUpdateAddress: () => {
    return useMutation({
      mutationKey: ["questionaire-or-address"],
      mutationFn: async (body: Pick<LeadSchemaDto, "_id" | "address">) => {
        const res = await api.patch(`/lead/questionaire-or-address`, { lead_id: body._id, address: body.address });
        return res.data?.result as LeadSchemaDto;
      },
      onSuccess: (data) => {
        return queryClient.invalidateQueries({ queryKey: ["lead", data.ray_id] });
      },
    });
  },
  useUpdateStatusOrRemarks: () => {
    return useMutation({
      mutationKey: ["status-or-remark"],
      // Todo: the typing is not exactly correct. lead_id is required, either status or remarks is required
      mutationFn: async (body: Partial<Pick<LeadSchemaDto, "_id" | "status" | "remarks">>) => {
        const finalBody: { lead_id?: string; status?: string; remarks?: string } = { lead_id: body._id };
        if (body.status) {
          finalBody["status"] = body.status;
        } else if (body.remarks) {
          finalBody["remarks"] = body.remarks;
        }
        const res = await api.patch(`/lead/status-or-remark`, finalBody);
        return res.data?.result as LeadSchemaDto;
      },
      onSuccess: (data) => {
        return queryClient.invalidateQueries({ queryKey: ["lead", data.ray_id] });
      },
    });
  },
  useGetLeadCases: (params: {
    batchId: string;
    page: number;
    limit: number;
    search?: string;
    filter: Partial<Record<keyof CaseSchemaDto, string>>;
    statuses?: string[];
    rangeOf?: string;
    rangeStart?: string;
    rangeEnd?: string;
    assigned_to?: string;
    assign_match?: boolean;
    customStatus?: "NOT_ASSIGNED" | "ASSIGNED";
    batchType: BATCH_TYPE;
  }) => {
    return useQuery({
      queryKey: ["lead", "case", params],
      queryFn: async () => {
        const { batchId, statuses, ...restParams } = params;
        const res = await api.get(`/lead/case${batchId ? `/${batchId}` : ""}`, {
          params: { ...restParams, statuses: statuses && statuses?.length > 0 ? statuses?.join(",") : undefined },
        });
        return res.data as T_Api_Success_Res<T_Api_Paginated<CaseSchemaDto>>;
      },
      // enabled: !!filters.batchId,
    });
  },
  useGetLeadCaseById: (caseId: string, populate: string[]) => {
    return useQuery({
      queryKey: ["lead", "case", caseId, populate],
      queryFn: async () => {
        const res = await api.get(`/lead/case/id/${caseId}`, {
          params: { logs: populate.includes("logs"), remarks: populate.includes("remarks") },
        });
        return res.data as T_Api_Success_Res<
          Omit<CaseSchemaDto, "logs" | "remarks"> & {
            logs?: Array<
              T_Populate<
                ElementOf<CaseSchemaDto["logs"]>,
                { user?: Pick<UserSchemaDto, "_id" | "email" | "name" | "profile_image" | "avatar_url"> }
              >
            >;
            remarks?: Array<
              T_Populate<
                ElementOf<CaseSchemaDto["remarks"]>,
                { user?: Pick<UserSchemaDto, "_id" | "email" | "name" | "profile_image" | "avatar_url"> }
              >
            >;
          }
        >;
      },
    });
  },
  useGetAllBatches: (params: { type: BATCH_TYPE }) => {
    return useQuery({
      queryKey: ["lead", "batch", "all", params],
      queryFn: async () => {
        const res = await api.get(`/lead/batch/all`, { params: params });
        return res.data as T_Api_Success_Res<CaseBatchSchemaDto[]>;
      },
    });
  },
  useUpdateCase: () => {
    return useMutation({
      mutationKey: ["lead", "case", "patch"],
      mutationFn: async ({ _id, ...payload }: Partial<CaseSchemaDto> & { _id: string }) => {
        const res = await api.patch(`/lead/case/${_id}`, payload);
        return res.data?.result as CaseSchemaDto;
      },
      onSuccess: (data, variables) => {
        return queryClient.invalidateQueries({ queryKey: ["lead", "case", variables._id] });
      },
    });
  },
  useDiscardedStep: () => {
    return useMutation({
      mutationKey: ["lead", "case", "discarded", "step"],
      mutationFn: async (body: { _id: string; step: PROCESS_STATUS }) => {
        const { _id, ...restBody } = body;
        await api.patch(`/lead/case/discarded/${_id}`, restBody);
      },
    });
  },
  useRunCases: () => {
    return useMutation({
      mutationKey: ["lead", "case", "process-case"],
      mutationFn: async (body: {
        method: "split" | "pc" | "bc" | "pcpl" | "discard";
        case_ids: string[];
        rerun: boolean;
      }) => {
        type T_Method = {
          pc: {
            messge: string;
            modified: number;
            linkedin_url_found: number;
            linkedin_url_not_found: number;
          };
          bc: any;
          split: any;
          pcpl: any;
          discard: any;
        };
        const { method, ...restBody } = body;
        const res = await api.post(`/lead/case/process-cases/${method}`, restBody);
        return res.data as T_Api_Success_Res<T_Method[typeof method]>;
      },
    });
  },
  useDownloadCases: () => {
    return useMutation({
      mutationKey: ["lead", "case", "download-csv"],
      mutationFn: async (body: { batch?: string; statuses: LeadStatus[] } | { case_ids?: string[] }) => {
        const res = await api.post(`/lead/batch/generate-csv`, body);
        const data = res.data as T_Api_Success_Res<{ path: string }>;
        const resFile = await api.get(`/lead/outputs/${data.result.path}`.replace("/api/v1/lead/outputs/", ""));
        return resFile.data;
      },
      onSuccess: (data, variables) => {
        const batchAll = queryClient.getQueryData(["lead", "batch", "all"]) as
          | T_Api_Success_Res<CaseBatchSchemaDto[]>
          | undefined;

        let filename = "file";
        if (batchAll && batchAll?.result?.length > 0) {
          if ("batch" in variables) {
            const foundBatch = batchAll?.result?.find((el) => el._id === variables?.batch);
            filename = `${foundBatch?.name}_${variables?.statuses?.join("-")}`;
          } else if ("case_ids" in variables) {
            filename = `Custom_cases_${variables?.case_ids?.length}`;
          }
        }
        // Create a Blob from the response data
        const blob = new Blob([data], { type: "text/csv" }); // Adjust MIME type if needed
        // Create a temporary URL for the Blob
        const downloadUrl = window.URL.createObjectURL(blob);
        // Create a temporary <a> element for download
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${filename}.csv`; // Set default file name
        document.body.appendChild(a);
        // Trigger download
        a.click();
        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      },
    });
  },
  useExportEmailFoundCasesCsv: () => {
    return useMutation({
      mutationKey: ["lead", "case", "export-csv/email-found"],
      mutationFn: async (body: { startDate?: string; endDate?: string; status: CASE_STATUS[] }) => {
        const res = await api.post(`/lead/export-csv/email-found`, body);
        const data = res.data as T_Api_Success_Res<{ path: string }>;
        const resFile = await api.get(`/lead/outputs/${data.result.path}`.replace("/api/v1/lead/outputs/", ""));
        return resFile.data;
      },
      onSuccess: (data, variables) => {
        const filename = `Email_found_cases_${variables?.startDate ? formatDate(variables?.startDate) : "start"}-${
          variables?.endDate ? formatDate(variables?.endDate) : formatDate(dayjs().toDate())
        }`;
        // Create a Blob from the response data
        const blob = new Blob([data], { type: "text/csv" }); // Adjust MIME type if needed
        // Create a temporary URL for the Blob
        const downloadUrl = window.URL.createObjectURL(blob);
        // Create a temporary <a> element for download
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${filename}.csv`; // Set default file name
        document.body.appendChild(a);
        // Trigger download
        a.click();
        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      },
    });
  },
  useAssignCases: () => {
    return useMutation({
      mutationKey: ["lead", "case", "assign"],
      mutationFn: async (body: { cases: string[]; assigned_to: string | undefined }) => {
        const res = await api.post(`/lead/case/assign`, body);
      },
      // onSuccess: (data, variables) => {
      //   return queryClient.invalidateQueries({ queryKey: ["lead", "case", variables._id] });
      // },
    });
  },
  useGetBatchStatus: (
    batchId: string,
    params: {
      assigned_to?: string;
      assign_match?: boolean;
    },
  ) => {
    return useQuery({
      queryKey: ["lead", "bacth", batchId, params],
      queryFn: async () => {
        const res = await api.get(`/lead/batch/status/${batchId}`, { params });
        const data = res.data as T_Api_Success_Res<{
          stats: Record<CASE_STATUS, number> & { total: number };
          hold: number;
        }>;

        return { stats: { hold: data.result?.hold, ...data.result?.stats } };
      },
    });
  },
  useGetPaginatedAssignments: (params: { startDate?: string; endDate?: string }) => {
    return useQuery({
      queryKey: ["assigment/all", params],
      queryFn: async () => {
        const res = await api.get(`/lead/case/assignment/report`, { params });
        return res.data?.result?.data as Assignment[];
      },
    });
  },
  useGetFileHeaders: () => {
    return useMutation({
      mutationKey: ["header/get"],
      mutationFn: async (file: FormData) => {
        const res = await api.post(`/lead/case/headers`, file);
        return res.data as T_Api_Success_Res<{ headers: string[]; previews: Record<string, string[]> }>;
      },
    });
  },
  useUploadDynamicCase: () => {
    return useMutation({
      mutationKey: ["case/upload"],
      mutationFn: async ({ mappedData }: { mappedData: FormData }) => {
        return api.post(`/lead/case-dynamic/upload`, mappedData);
      },
    });
  },
  useAddLogEntry: () => {
    return useMutation({
      mutationFn: async ({ id, ...payload }: { id: T_MongoId; message: string }) => {
        return await api.patch(`/lead/logs/${id}`, payload);
      },
    });
  },
  useAddRemark: () => {
    return useMutation({
      mutationFn: async ({ id, ...payload }: { id: T_MongoId; message: string; rating?: number }) => {
        return await api.patch(`/lead/remarks/${id}`, payload);
      },
    });
  },

  useOpenDET: () => {
    return useMutation({
      mutationKey: ["lead/det-access"],
      mutationFn: async () => {
        const res = await api.post(`/lead/det-access`);
        return res.data as T_Api_Success_Res<{ url: string }>;
      },
    });
  },
};
